import React, { useLayoutEffect, useRef } from 'react';
import OPT from '../../lib/optImages.json';

const find = (src) => (typeof src === 'string' ? OPT.find((o) => src.includes(o.key)) : null);
const set = (o, fmt) => o.widths.map((w) => `/img/opt/${o.name}-${w}.${fmt} ${w}w`).join(', ');

// <img> drop-in: serves AVIF → WebP → original with srcset when an optimized version exists.
// If an optimized variant fails, drop the <source>s so the original (or caller's onError) takes over.
const fallback = (onError) => (e) => {
  const img = e.currentTarget;
  if (img.currentSrc.includes('/img/opt/') && img.parentElement?.tagName === 'PICTURE') {
    img.parentElement.querySelectorAll('source').forEach((n) => n.remove());
    img.setAttribute('src', img.getAttribute('src'));
    return;
  }
  if (onError) onError(e);
};

export const OptImg = ({ src, sizes, priority = false, loading, onError, ...rest }) => {
  // Cover-cropped heroes render wider than the viewport on portrait phones.
  const sz = sizes || (priority ? '(max-width: 767px) 170vw, 100vw' : '100vw');
  const o = find(src);
  const ref = useRef(null);
  // Set src only once the <img> is inside <picture>, otherwise React's detached <img> fetches the original too.
  useLayoutEffect(() => { if (o && ref.current && ref.current.getAttribute('src') !== src) ref.current.setAttribute('src', src); }, [o, src]);
  const load = priority ? 'eager' : loading;
  const prio = priority ? { fetchPriority: 'high' } : {};
  if (!o) return <img src={src} loading={load} decoding="async" onError={onError} {...prio} {...rest} />;
  return (
    <picture style={{ display: 'contents' }}>
      <source type="image/avif" srcSet={set(o, 'avif')} sizes={sz} />
      <source type="image/webp" srcSet={set(o, 'webp')} sizes={sz} />
      <img ref={ref} width={o.w} height={o.h} loading={load} decoding="async" onError={fallback(onError)} {...prio} {...rest} />
    </picture>
  );
};

// Single optimized WebP URL (for CSS backgrounds/textures); falls back to the original.
export const optUrl = (src, width) => {
  const o = find(src);
  if (!o) return src;
  const w = o.widths.find((x) => x >= (width || 0)) || o.widths[o.widths.length - 1];
  return `/img/opt/${o.name}-${w}.webp`;
};
